import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Skeleton,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { LayoutTemplate, Loader2 } from 'lucide-react';
import { z } from 'zod';

import { DatePicker } from '@/components/form/inputs/date-picker';
import { WorkstreamSchema } from '@/project-management/entities/workstream';
import { AddWorkstreamMutation } from '@/project-management/mutations/add-workstream.mutation';
import { WorkstreamTemplatesQuery } from '@/project-management/queries/workstream-templates.query';
import { cls } from '@/utils/cls';
import { label } from '@/utils/format/label';

export type AddWorkstreamTemplateFormProps = {
  onClose: () => void;
};

const TemplateFormSchema = WorkstreamSchema.pick({
  startDate: true,
  endDate: true,
})
  .extend({ templateId: z.string() })
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      return data.startDate <= data.endDate;
    },
    {
      message: 'End date must be after start date',
      path: ['endDate'],
    },
  );
type TemplateForm = z.infer<typeof TemplateFormSchema>;

export function AddWorkstreamTemplateForm({
  onClose,
}: AddWorkstreamTemplateFormProps) {
  const { siteId } = useParams<{ siteId: string }>();
  const templatesQuery = WorkstreamTemplatesQuery.useQuery();
  const addWorkstream = AddWorkstreamMutation.useMutation();
  const form = useForm<TemplateForm>({
    resolver: zodResolver(TemplateFormSchema),
  });

  async function handleSubmit(data: TemplateForm) {
    await toast.promise(addWorkstream.mutateAsync({ siteId, template: data }), {
      pending: 'Creating workstream from template...',
      success: 'Workstream created successfully.',
      error: 'Failed to create workstream. Please try again.',
    });
    onClose();
  }

  const templateSelected = Boolean(form.watch('templateId'));

  if (templatesQuery.isError) {
    throw templatesQuery.error;
  }

  return (
    <>
      <Form {...form}>
        <form
          id="add-workstream-template-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <FormField
            control={form.control}
            name="templateId"
            render={({ field }) => (
              <FormItem className="gap-2">
                <FormLabel>Select Template</FormLabel>
                <FormControl>
                  <div className="space-y-3">
                    {templatesQuery.isLoading
                      ? [1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className="rounded-lg border border-gray-200 p-3"
                          >
                            <div className="grid grid-cols-[min-content_1fr] items-start gap-3">
                              <Skeleton className="size-9 rounded-md" />
                              <Skeleton className="h-16 w-full rounded-md" />
                            </div>
                          </div>
                        ))
                      : (templatesQuery.data?.templates.map((template) => (
                          <div
                            key={template.id}
                            className={cls(
                              'cursor-pointer rounded-lg border p-3 transition-colors',
                              field.value === template.id
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300',
                            )}
                            onClick={() => field.onChange(template.id)}
                          >
                            <div className="grid grid-cols-[min-content_1fr] items-start gap-3">
                              <div className="rounded-md bg-gray-100 p-2">
                                <LayoutTemplate className="size-5 text-gray-600" />
                              </div>
                              <div>
                                <h4 className="font-medium">{template.name}</h4>
                                <p className="text-sm text-gray-600">
                                  {template.description}
                                </p>
                                <p className="mt-1 text-xs text-gray-500">
                                  {template.nbTasks} predefined{' '}
                                  {label(['task', 'tasks'], template.nbTasks)}
                                </p>
                              </div>
                            </div>
                          </div>
                        )) ?? null)}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {templateSelected ? (
            <>
              <div className="grid items-start gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onSelect={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onSelect={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </>
          ) : null}
        </form>
      </Form>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-workstream-template-form"
          disabled={addWorkstream.isLoading || !templateSelected}
        >
          {addWorkstream.isLoading ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Creating...
            </>
          ) : (
            <>Create Workstream</>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
